import { lazy, Suspense } from "react";

const Dashboard = lazy(() => import("../components/templates/home/Dashboard.jsx"));
const Detail = lazy(() => import("../components/templates/home/Detail.jsx"));
const MapV2 = lazy(() => import("../components/templates/home/MapV2.jsx"));

// v2 overhaul. Lazy, so a v1-layout visitor (still reachable via ?layout=
// classic) never pays for v2's bundle, and vice versa.
const V2Home = lazy(() => import("../v2/templates/V2Home.jsx"));
const V2Map = lazy(() => import("../v2/templates/V2Map.jsx"));
const V2Projects = lazy(() => import("../v2/templates/V2Projects.jsx"));
const V2Alerts   = lazy(() => import("../v2/templates/V2Alerts.jsx"));
const V2AlertDetail = lazy(() => import("../v2/templates/V2AlertDetail.jsx"));
const V2More     = lazy(() => import("../v2/templates/V2More.jsx"));
const V2Message  = lazy(() => import("../v2/templates/V2Message.jsx"));
const V2Meetings = lazy(() => import("../v2/templates/V2Meetings.jsx"));
const V2Expert   = lazy(() => import("../v2/templates/V2Expert.jsx"));
const V2Report   = lazy(() => import("../v2/templates/V2Report.jsx"));
const V2About    = lazy(() => import("../v2/templates/V2About.jsx"));
const V2FAQ      = lazy(() => import("../v2/templates/V2FAQ.jsx"));
const V2Privacy       = lazy(() => import("../v2/templates/V2Privacy.jsx"));
const V2Terms         = lazy(() => import("../v2/templates/V2Terms.jsx"));
const V2Accessibility = lazy(() => import("../v2/templates/V2Accessibility.jsx"));
const V2Detail   = lazy(() => import("../v2/templates/V2Detail.jsx"));
const V2TemplatingTest = lazy(() => import("../v2/templates/V2TemplatingTest.jsx"));
const V2Stub     = lazy(() => import("../v2/templates/V2Stub.jsx"));

/**
 * Template registry: maps template key to component.
 * Adding a new template only requires adding it here and creating the component.
 */
export const TEMPLATES = {
  dashboard: Dashboard,
  detail: Detail,
  mapv2: MapV2,
  v2home: V2Home,
  v2map: V2Map,
  v2projects: V2Projects,
  v2alerts: V2Alerts,
  v2alertdetail: V2AlertDetail,
  v2more: V2More,
  v2meetings: V2Meetings,
  v2expert:   V2Expert,
  v2message:  V2Message,
  v2report:   V2Report,
  v2faq:      V2FAQ,
  v2about:    V2About,
  v2privacy:       V2Privacy,
  v2terms:         V2Terms,
  v2accessibility: V2Accessibility,
  v2detail:        V2Detail,
  v2templatingtest: V2TemplatingTest,
};

/**
 * Templates currently enabled for production use. v1's dashboard/detail/mapv2
 * stay enabled (not removed) for rollback — reachable via ?layout=classic —
 * rather than cutting that path off as part of this same change. v2templatingtest
 * stays excluded: it's a dev-only harness proving the templating engine works,
 * not a real page. Others remain available via ?template= for dev regardless
 * of this set.
 */
export const ENABLED_TEMPLATES = new Set([
  "dashboard", "detail", "mapv2",
  "v2home", "v2map", "v2projects", "v2alerts", "v2alertdetail",
  "v2more", "v2meetings", "v2expert", "v2message", "v2report", "v2faq",
  "v2about", "v2privacy", "v2terms", "v2accessibility", "v2detail",
]);

export const DEFAULT_TEMPLATE = "v2home";

/**
 * Resolves which template to render.
 * Priority: URL param ?template= (dev, any template) > templateOverride (system_pages, enabled only) > homepageTemplate (settings, enabled only) > default
 */
function resolveTemplate(templateOverride, homepageTemplate) {
  // URL param for dev/testing — allows ANY template
  const params = new URLSearchParams(window.location.search);
  const urlTemplate = params.get("template");
  if (urlTemplate && TEMPLATES[urlTemplate]) return urlTemplate;

  // system_pages override (DB-driven routing) — enabled only
  if (templateOverride && TEMPLATES[templateOverride] && ENABLED_TEMPLATES.has(templateOverride))
    return templateOverride;

  // Settings-backed selection — enabled only
  if (homepageTemplate && TEMPLATES[homepageTemplate] && ENABLED_TEMPLATES.has(homepageTemplate))
    return homepageTemplate;

  return DEFAULT_TEMPLATE;
}

export default function Home({ settings, statuses, categories, homepageTemplate, templateOverride }) {
  const templateKey = resolveTemplate(templateOverride, homepageTemplate);
  const TemplateComponent = TEMPLATES[templateKey];

  return (
    <Suspense fallback={null}>
      <TemplateComponent
        settings={settings}
        statuses={statuses}
        categories={categories}
      />
    </Suspense>
  );
}
